import type { Draw } from '../../components/figures/CanvasFigure';
import { dot, easeInOut, lerp } from '../../components/figures/draw';

// The closed loop. A model decides, people review the answers it is unsure of, the reviews
// train the next version, and that version serves only once it passes its gate. Each pass
// through the gate brings a new version, and the rings settle as the model improves. Each
// stage has a colour of its own (Ink.cycle), and the bead carries the last one it passed.
const LAP = 8;
const STAGES = ['DECIDE', 'REVIEW', 'TRAIN', 'GATE'];
const FAMILY = 'support-router';
// How far the rings waver for each of the three versions shown, from the first to the third.
const WAVER = [1, 0.58, 0.3];
const RINGS = 11;
const TAU = Math.PI * 2;
const LABEL_FONT = '500 11px "IBM Plex Mono", monospace';

// With reduced motion: the third version, the bead on its way from DECIDE to REVIEW.
export const LOOP_REST = 17;

// As large as the box allows; on a narrow screen the rings draw closer and the loop gives
// way, so the side labels still fit beside it.
function fit(w: number, h: number, labelWidth: number) {
  const size = (spacing: number) => {
    const gap = (spacing * (RINGS - 1)) / 2 + 10;
    return { spacing, gap, radius: Math.min(Math.min(w, h) * 0.32, w / 2 - gap - labelWidth - 2) };
  };
  const roomy = size(5.5);
  return roomy.radius >= 80 ? roomy : size(4);
}

export const closedLoop: Draw = (ctx, w, h, t, ink) => {
  const cx = w / 2;
  const cy = h / 2;
  ctx.font = LABEL_FONT;
  const { spacing, gap, radius } = fit(w, h, Math.max(ctx.measureText(STAGES[1]).width, ctx.measureText(STAGES[3]).width));
  // The bead starts at DECIDE, at the top, and goes clockwise; the gate is three quarters round.
  const turn = t / LAP;
  const passes = Math.floor(turn + 0.25);
  const since = (turn + 0.25 - passes) * LAP;
  const version = passes % 3;
  const waver = passes ? lerp(WAVER[(version + 2) % 3], WAVER[version], easeInOut(since / 1.2)) : WAVER[0];
  const at = (a: number, r: number): [number, number] => {
    const rr = r + waver * (Math.sin(a * 3 + t * 0.5) * 2 + Math.sin(a * 5 - t * 0.3) * 1.5);
    return [cx + rr * Math.cos(a), cy + rr * Math.sin(a)];
  };

  for (let i = 0; i < RINGS; i++) {
    const r = radius + (i - (RINGS - 1) / 2) * spacing;
    ctx.beginPath();
    for (let s = 0; s <= 120; s++) {
      const [x, y] = at((s / 120) * TAU, r);
      if (s) ctx.lineTo(x, y);
      else ctx.moveTo(x, y);
    }
    ctx.closePath();
    ctx.strokeStyle = ink.line(i % 3 === 0 ? 0.18 : 0.1);
    ctx.lineWidth = i % 3 === 0 ? 1.1 : 0.8;
    ctx.stroke();
  }

  // Each stage is a mark on the loop in its colour; the one the bead passed last is labelled in ink.
  const stageAt = (u: number) => Math.floor((((u % 1) + 1) % 1) * 4);
  const current = stageAt(turn);
  ctx.textBaseline = 'middle';
  STAGES.forEach((stage, k) => {
    const a = -Math.PI / 2 + (k * TAU) / 4;
    const [x, y] = at(a, radius);
    dot(ctx, x, y, 2.8, ink.cycle[k]);
    ctx.fillStyle = ink.line(k === current ? 0.8 : 0.4);
    ctx.textAlign = k === 1 ? 'left' : k === 3 ? 'right' : 'center';
    const ox = k === 1 ? radius + gap : k === 3 ? -radius - gap : 0;
    const oy = k === 0 ? -radius - gap - 8 : k === 2 ? radius + gap + 8 : 0;
    ctx.fillText(stage, cx + ox, cy + oy);
  });

  // The bead, with a short fading trail that keeps the colour of the stage each dot had passed.
  const head = -Math.PI / 2 + (turn % 1) * TAU;
  for (let k = 5; k >= 1; k--) {
    const [x, y] = at(head - k * 0.07, radius);
    ctx.globalAlpha = (1 - k / 6) * 0.5;
    dot(ctx, x, y, 2 - k * 0.2, ink.cycle[stageAt(turn - (k * 0.07) / TAU)]);
  }
  ctx.globalAlpha = 1;
  const [bx, by] = at(head, radius);
  dot(ctx, bx, by, 3.4, ink.cycle[current]);

  // The family and the version now serving, which fades in as it passes the gate. The
  // family's name shows only where the inner ring has room for it.
  const inner = radius - ((RINGS - 1) / 2) * spacing;
  ctx.textAlign = 'center';
  ctx.font = '400 11px "IBM Plex Mono", monospace';
  const named = ctx.measureText(FAMILY).width < 2 * inner - 20;
  if (named) {
    ctx.fillStyle = ink.line(0.45);
    ctx.fillText(FAMILY, cx, cy - 20);
  }
  ctx.globalAlpha = passes ? easeInOut(since / 0.6) : 1;
  ctx.fillStyle = ink.line(0.88);
  ctx.font = `400 ${Math.round(Math.min(44, inner * 0.7))}px "Crimson Pro", Georgia, serif`;
  ctx.fillText(`@${version + 1}`, cx, named ? cy + 12 : cy + 2);
  ctx.globalAlpha = 1;
};
